/**
 * Kaksos Portal - My Keeper Page (Loop 1 step four, cut 7 (d), Ruling M; GOAL_KeeperKey_v0_1 W-2c, Ruling 6 of 8 Oct 2026)
 *
 * The one doorway through which the outside world reaches the owner's Kaksos:
 * the Keeper Agent's seat and its key. This page is where the owner seats the
 * Keeper, moves it between Inner, Mid and Outer, issues its link, disconnects it
 * and removes it. The Keeper is the Kaksos's own kind of identity, never a peeler;
 * the API mints its id (keeper- and 32 hex) and it is never at centre.
 *
 * Routes used, all existing: GET /api/circles/members (the seat whose peeler_id
 * starts with keeper-), POST /api/circles/keeper, PUT /api/circles/members/:id,
 * POST /api/connections/members/:id/issue-link, PUT .../switch (false),
 * DELETE /api/circles/members/:id.
 *
 * The issued link lives in component state only and is shown once (WISDOM 19/22):
 * never the browser store, never the URL, never a console line.
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { circlesApi, settingsApi, CircleMember, CircleLevel, IssueLinkResponse } from '../lib/api';
import DashboardLayout from '../components/DashboardLayout';
import BobyModal from '../components/BobyModal';

const KEEPER_ID_PREFIX = 'keeper-';
const isKeeperSeat = (member: CircleMember) => String(member.peeler_id || '').startsWith(KEEPER_ID_PREFIX);

// The three circles a Keeper may hold. Centre is the owner's seat alone (the API refuses it); Public is a legacy tier.
const KEEPER_CIRCLES: { level: CircleLevel; label: string; color: string; bgColor: string }[] = [
    { level: 'inner', label: 'Inner', color: 'text-green-700', bgColor: 'bg-green-100' },
    { level: 'mid', label: 'Mid', color: 'text-yellow-700', bgColor: 'bg-yellow-100' },
    { level: 'outer', label: 'Outer', color: 'text-orange-700', bgColor: 'bg-orange-100' },
];

function circleLabel(level: string): string {
    const found = KEEPER_CIRCLES.find(c => c.level === level);
    if (found) return found.label;
    if (level === 'center') return 'Centre';
    return level.charAt(0).toUpperCase() + level.slice(1);
}

export default function MyKeeperPage() {
    const { user } = useAuth();
    const bobyPlaceId = user?.homeBobyPlaceId || user?.id || '';

    // The Keeper's seat, read across all circles
    const [keeper, setKeeper] = useState<CircleMember | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);

    // Add Keeper form (no Keeper yet)
    const [form, setForm] = useState({ name: 'Keeper', circleLevel: 'inner' as CircleLevel });
    const [isAdding, setIsAdding] = useState(false);

    // Actions on the seated Keeper
    const [actionError, setActionError] = useState<string | null>(null);
    const [isChanging, setIsChanging] = useState(false);
    const [isIssuing, setIsIssuing] = useState(false);
    const [issuedLink, setIssuedLink] = useState<IssueLinkResponse | null>(null);
    const [linkCopied, setLinkCopied] = useState(false);
    const [isDisconnecting, setIsDisconnecting] = useState(false);
    const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
    const [isRemoving, setIsRemoving] = useState(false);

    // Notes (Ruling 7 of 8 Oct 2026): the Keeper's relationship notes live here, worded for an agent. They feed the
    // roster the Kaksos reads (prompt-assembly.js loadCircleRoster), so the Kaksos knows what its Keeper is.
    const [notes, setNotes] = useState('');
    const [isSavingNotes, setIsSavingNotes] = useState(false);
    // The Kaksos's own name for the notes question; empty means "your Kaksos"
    const [kaksosName, setKaksosName] = useState('');

    const loadKeeper = useCallback(async () => {
        if (!bobyPlaceId) return;
        setLoadError(null);
        try {
            const response = await circlesApi.listMembers(bobyPlaceId, 'all');
            const seat = response.members.find(isKeeperSeat) || null;
            setKeeper(seat);
            setNotes(seat?.relationship_notes || '');
        } catch (err) {
            setLoadError(err instanceof Error ? err.message : 'Failed to load your Keeper');
        } finally {
            setIsLoading(false);
        }
    }, [bobyPlaceId]);

    useEffect(() => {
        loadKeeper();
    }, [loadKeeper]);

    useEffect(() => {
        async function fetchKaksosName() {
            if (!bobyPlaceId) return;
            try {
                const response = await settingsApi.getSettings(bobyPlaceId);
                const name = (response.settings?.kaksos_name || '').trim();
                setKaksosName(name === 'Kaksos' ? '' : name);
            } catch {
                setKaksosName('');
            }
        }
        fetchKaksosName();
    }, [bobyPlaceId]);

    // Save the notes on blur, only when they changed
    async function handleSaveNotes() {
        if (!keeper) return;
        if (notes === (keeper.relationship_notes || '')) return;
        setIsSavingNotes(true);
        setActionError(null);
        try {
            await circlesApi.updateMember(keeper.id, bobyPlaceId, { relationshipNotes: notes });
            await loadKeeper();
        } catch (err) {
            setActionError(err instanceof Error ? err.message : 'Failed to save the notes');
        } finally {
            setIsSavingNotes(false);
        }
    }

    // Seat the Keeper
    async function handleAddKeeper(e: React.FormEvent) {
        e.preventDefault();
        setIsAdding(true);
        setActionError(null);
        try {
            await circlesApi.addKeeper(bobyPlaceId, form.name.trim() || 'Keeper', form.circleLevel);
            setForm({ name: 'Keeper', circleLevel: 'inner' });
            await loadKeeper();
        } catch (err) {
            setActionError(err instanceof Error ? err.message : 'Failed to add the Keeper');
        } finally {
            setIsAdding(false);
        }
    }

    // Change Circle: Inner, Mid or Outer only
    async function handleChangeCircle(level: CircleLevel) {
        if (!keeper || keeper.circle_level === level) return;
        setIsChanging(true);
        setActionError(null);
        try {
            await circlesApi.updateMember(keeper.id, bobyPlaceId, { circleLevel: level });
            await loadKeeper();
        } catch (err) {
            setActionError(err instanceof Error ? err.message : 'Failed to change the circle');
        } finally {
            setIsChanging(false);
        }
    }

    // Issue Keeper link: the Agent link route on the Keeper's seat; a new link ends the old one
    async function handleIssueLink() {
        if (!keeper) return;
        setIsIssuing(true);
        setActionError(null);
        try {
            const reply = await circlesApi.issueLink(bobyPlaceId, keeper.id);
            setLinkCopied(false);
            setIssuedLink(reply);
            await loadKeeper();
        } catch (err) {
            setActionError(err instanceof Error ? err.message : 'Failed to issue the link');
        } finally {
            setIsIssuing(false);
        }
    }

    async function handleCopyLink() {
        if (!issuedLink) return;
        try {
            await navigator.clipboard.writeText(issuedLink.claim_url);
            setLinkCopied(true);
            setTimeout(() => setLinkCopied(false), 2000);
        } catch {
            setLinkCopied(false);
        }
    }

    // Disconnect: ends the live key, keeps the seat
    async function handleDisconnect() {
        if (!keeper) return;
        setIsDisconnecting(true);
        setActionError(null);
        try {
            await circlesApi.setSwitch(bobyPlaceId, keeper.id, false);
            setIssuedLink(null);
            await loadKeeper();
        } catch (err) {
            setActionError(err instanceof Error ? err.message : 'Failed to disconnect the Keeper');
        } finally {
            setIsDisconnecting(false);
        }
    }

    // Remove Keeper: ends the key at once and deletes the seat
    async function handleRemoveKeeper() {
        if (!keeper) return;
        setIsRemoving(true);
        setActionError(null);
        try {
            await circlesApi.removeMember(keeper.id, bobyPlaceId);
            setIssuedLink(null);
            setShowRemoveConfirm(false);
            await loadKeeper();
        } catch (err) {
            setActionError(err instanceof Error ? err.message : 'Failed to remove the Keeper');
            setShowRemoveConfirm(false);
        } finally {
            setIsRemoving(false);
        }
    }

    function formatExpiry(iso: string): string {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return iso;
        return d.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
    }

    return (
        <DashboardLayout>
            <div className="max-w-3xl mx-auto px-4 py-6">
                {/* Header */}
                <div className="mb-6">
                    <h1 className="text-2xl font-bold text-gray-800">My Keeper</h1>
                </div>

                <div className="bg-white rounded-xl border border-gray-200 p-4">
                    <p className="text-gray-700 mb-4">
                        Your Keeper Agent is the one doorway through which the outside world reaches your Kaksos. It holds a single key, issued here and replaced whenever you issue a new one.
                    </p>

                    {loadError && (
                        <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm mb-4">
                            {loadError}
                        </div>
                    )}
                    {actionError && (
                        <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm mb-4">
                            {actionError}
                        </div>
                    )}

                    {isLoading ? (
                        <p className="text-sm text-gray-500">Loading...</p>
                    ) : !keeper ? (
                        /* No Keeper yet: seat one */
                        <form onSubmit={handleAddKeeper} className="space-y-4">
                            <p className="text-sm text-gray-600">
                                Your Keeper has no seat yet. Give it a name and a circle to seat it. It is never at centre.
                            </p>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                                <input
                                    type="text"
                                    value={form.name}
                                    onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                                    maxLength={100}
                                    placeholder="Keeper"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Circle</label>
                                <select
                                    value={form.circleLevel}
                                    onChange={(e) => setForm(prev => ({ ...prev, circleLevel: e.target.value as CircleLevel }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                                >
                                    {KEEPER_CIRCLES.map(c => (
                                        <option key={c.level} value={c.level}>{c.label}</option>
                                    ))}
                                </select>
                            </div>
                            <button
                                type="submit"
                                disabled={isAdding || !form.name.trim()}
                                className="w-full py-3 bg-amber-500 text-white rounded-lg font-medium hover:bg-amber-600 transition-colors disabled:opacity-50"
                            >
                                {isAdding ? 'Adding...' : 'Add Keeper'}
                            </button>
                            <button
                                type="button"
                                disabled
                                className="w-full py-3 bg-amber-500 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Issue Keeper link
                            </button>
                            <p className="text-sm text-gray-500">Available when your Keeper Agent has a seat.</p>
                        </form>
                    ) : (
                        /* The seated Keeper */
                        <div className="space-y-5">
                            <div>
                                <label className="text-xs font-semibold text-gray-500 uppercase">Name</label>
                                <p className="mt-1 text-gray-800 font-medium">
                                    {keeper.peeler_name || 'Keeper'}
                                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">Keeper</span>
                                </p>
                                <p className="mt-1 text-sm text-gray-500">Agent seat, no TelePathCode.</p>
                            </div>

                            <div>
                                <label className="text-xs font-semibold text-gray-500 uppercase">Circle</label>
                                <p className="mt-1 text-gray-800">{circleLabel(keeper.circle_level)}</p>
                                <label className="text-xs font-semibold text-gray-500 uppercase mt-3 mb-2 block">Change Circle</label>
                                <div className="flex flex-wrap gap-2">
                                    {KEEPER_CIRCLES.map(circle => (
                                        <button
                                            key={circle.level}
                                            type="button"
                                            onClick={() => handleChangeCircle(circle.level)}
                                            disabled={isChanging || keeper.circle_level === circle.level}
                                            className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                                                keeper.circle_level === circle.level
                                                    ? `${circle.bgColor} ${circle.color} ring-2 ring-offset-1`
                                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                            } disabled:opacity-50`}
                                        >
                                            {circle.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <label className="text-xs font-semibold text-gray-500 uppercase">Key</label>
                                <p className={`mt-1 text-sm font-medium ${keeper.switch_on ? 'text-green-700' : 'text-gray-600'}`}>
                                    {keeper.switch_on ? 'Key live' : 'No live key'}
                                </p>
                            </div>

                            <div>
                                <label className="text-xs font-semibold text-gray-500 uppercase">Notes</label>
                                <textarea
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    onBlur={handleSaveNotes}
                                    placeholder={`What should ${kaksosName || 'your Kaksos'} know about its Keeper?`}
                                    rows={3}
                                    className="mt-1 w-full px-3 py-2 text-sm text-gray-700 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                                />
                                {isSavingNotes && (
                                    <p className="text-xs text-gray-400 mt-1">Saving...</p>
                                )}
                            </div>

                            {issuedLink && (
                                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3">
                                    <p className="text-sm text-gray-700">This link is shown once. Issuing a new link ends the old one.</p>
                                    <input
                                        type="text"
                                        readOnly
                                        value={issuedLink.claim_url}
                                        onFocus={(e) => e.currentTarget.select()}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm text-gray-800 font-mono"
                                    />
                                    <p className="text-sm text-gray-600">Works until {formatExpiry(issuedLink.token_expires_at)}</p>
                                    {issuedLink.revoked_prior > 0 && (
                                        <p className="text-sm text-gray-600">This replaces the earlier link.</p>
                                    )}
                                    <div className="flex gap-2">
                                        <button
                                            type="button"
                                            onClick={handleCopyLink}
                                            className="flex-1 py-2 px-4 bg-amber-500 text-white rounded-lg font-medium hover:bg-amber-600 transition-colors"
                                        >
                                            {linkCopied ? 'Copied' : 'Copy link'}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setIssuedLink(null)}
                                            className="flex-1 py-2 px-4 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                                        >
                                            Done
                                        </button>
                                    </div>
                                </div>
                            )}

                            <div className="space-y-2 pt-2 border-t border-gray-100">
                                <button
                                    type="button"
                                    onClick={handleIssueLink}
                                    disabled={isIssuing || isDisconnecting || isRemoving}
                                    className="w-full py-3 bg-amber-500 text-white rounded-lg font-medium hover:bg-amber-600 transition-colors disabled:opacity-50"
                                >
                                    {isIssuing ? 'Issuing...' : 'Issue Keeper link'}
                                </button>
                                {keeper.switch_on && (
                                    <button
                                        type="button"
                                        onClick={handleDisconnect}
                                        disabled={isIssuing || isDisconnecting || isRemoving}
                                        className="w-full py-3 bg-white text-amber-700 border border-amber-500 rounded-lg font-medium hover:bg-amber-50 transition-colors disabled:opacity-50"
                                    >
                                        {isDisconnecting ? 'Disconnecting...' : 'Disconnect'}
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={() => setShowRemoveConfirm(true)}
                                    disabled={isIssuing || isDisconnecting || isRemoving}
                                    className="w-full py-3 bg-red-50 text-red-600 rounded-lg font-medium hover:bg-red-100 transition-colors disabled:opacity-50"
                                >
                                    Remove Keeper
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Remove Keeper confirmation */}
                <BobyModal
                    variant="confirm"
                    isOpen={showRemoveConfirm}
                    onClose={() => setShowRemoveConfirm(false)}
                    title="Remove Keeper"
                    message={`Remove ${keeper?.peeler_name || 'your Keeper'}? Its key ends at once and its seat is removed. You can seat a new Keeper afterwards.`}
                    confirmLabel="Remove Keeper"
                    destructive
                    onConfirm={handleRemoveKeeper}
                />
            </div>
        </DashboardLayout>
    );
}
